
import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSupport } from '../../hooks/useSupport';
import { openEmailClient } from '../../services/emailService';

export default function Suporte() {
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    assunto: '',
    mensagem: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const { supportData, loading, error } = useSupport();
  
  // Definir categoria ativa baseada nas categorias disponíveis
  const [activeCategory, setActiveCategory] = useState<string>('');

  // Mapear categorias da planilha para configuração local com ícones
  const categoryConfig: { [key: string]: { name: string; icon: string } } = {
    'instalacao': { name: 'Instalação', icon: 'ri-tools-line' },
    'compatibilidade': { name: 'Compatibilidade', icon: 'ri-checkbox-circle-line' },
    'manutencao': { name: 'Manutenção', icon: 'ri-settings-3-line' },
    'manutenção': { name: 'Manutenção', icon: 'ri-settings-3-line' },
    'garantia': { name: 'Garantia', icon: 'ri-shield-check-line' },
    'pedidos': { name: 'Pedidos', icon: 'ri-shopping-cart-line' },
    'produto': { name: 'Produto', icon: 'ri-product-hunt-line' },
    'entrega': { name: 'Entrega', icon: 'ri-truck-line' },
    'pagamento': { name: 'Pagamento', icon: 'ri-bank-card-line' }
  };

  // Gerar categorias dinamicamente baseado nos dados da planilha
  const supportCategories = useMemo(() => {
    const categoriesFromData = new Set(supportData.map(item => item.CATEGORY.toLowerCase().trim()));
    return Array.from(categoriesFromData).map(categoryKey => {
      const config = categoryConfig[categoryKey] || { 
        name: categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1), 
        icon: 'ri-question-line' 
      };
      
      return {
        id: categoryKey,
        name: config.name,
        icon: config.icon
      };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [supportData]);

  // Organizar dados do FAQ por categoria
  const organizedFAQData = useMemo(() => {
    const organized: { [key: string]: Array<{question: string, answer: string}> } = {};
    
    supportData.forEach(item => {
      const category = item.CATEGORY.toLowerCase().trim();
      
      // Usar a categoria diretamente
      let categoryKey = category;
      
      if (!organized[categoryKey]) {
        organized[categoryKey] = [];
      }
      
      organized[categoryKey].push({
        question: item.TITLE,
        answer: item.ANSWER
      });
    });
    
    return organized;
  }, [supportData]);

  // Definir categoria inicial quando os dados carregarem
  useEffect(() => {
    if (supportCategories.length > 0 && !activeCategory) {
      setActiveCategory(supportCategories[0].id);
    }
  }, [supportCategories, activeCategory]);

  const contactMethods = [
    {
      title: 'WhatsApp',
      description: 'Atendimento rápido para dúvidas urgentes',
      icon: 'ri-whatsapp-line',
      contact: '(11) 99154-2774',
      action: 'Chamar no WhatsApp',
      color: 'bg-green-500'
    },
    {
      title: 'Email',
      description: 'Para dúvidas técnicas detalhadas',
      icon: 'ri-mail-line',
      contact: 'suporte@gzparts.com.br',
      action: 'Enviar Email',
      color: 'bg-blue-500'
    }
  ];

  const filteredFAQ = organizedFAQData[activeCategory]?.filter(item =>
    item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.answer.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Validar campos obrigatórios
      if (!formData.nome || !formData.email || !formData.assunto || !formData.mensagem) {
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }

      // Abrir cliente de email
      const success = openEmailClient(formData);

      if (success) {
        setSubmitStatus('success');
        // Limpar formulário após 2 segundos
        setTimeout(() => {
          setFormData({
            nome: '',
            email: '',
            telefone: '',
            assunto: '',
            mensagem: ''
          });
          setSubmitStatus('');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="bg-gradient-to-r from-teal-600 to-teal-700 py-16 relative"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(13, 148, 136, 0.9), rgba(15, 118, 110, 0.9)), url('/images/suporte-hero.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Suporte Técnico
            </h1>
            <p className="text-xl text-teal-100 max-w-3xl mx-auto mb-8">
              Nossa equipe especializada está aqui para te ajudar com a instalação, compatibilidade e manutenção do seu veículo.
            </p>
            
            {/* Busca */}
            <div className="max-w-md mx-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar na base de conhecimento..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 pl-12 rounded-lg border-0 focus:ring-2 focus:ring-white focus:ring-opacity-50 text-gray-900"
                />
                <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categorias e FAQ */}
      <div>
        { supportCategories.length > 0 && (
            <section className="py-16">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                  {/* Sidebar de Categorias */}
                  <div className="lg:col-span-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Categorias</h3>
                    <div className="space-y-2">
                      {supportCategories.map((category) => (
                        <button
                          key={category.id}
                          onClick={() => setActiveCategory(category.id)}
                          className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 cursor-pointer ${
                            activeCategory === category.id
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <i className={`${category.icon} text-lg`}></i>
                          {category.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* FAQ Content */}
                  <div className="lg:col-span-3">
                    {loading && (
                      <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                        <p className="mt-4 text-gray-600">Carregando perguntas frequentes...</p>
                      </div>
                    )}

                    {error && (
                      <div className="text-center py-12">
                        <i className="ri-error-warning-line text-4xl text-red-400 mb-4"></i>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Erro ao carregar FAQ</h3>
                        <p className="text-gray-600 mb-6">{error}</p>
                      </div>
                    )}

                    {!loading && !error && (
                      <>
                        <div className="mb-6">
                          <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            {supportCategories.find(cat => cat.id === activeCategory)?.name}
                          </h2>
                          <p className="text-gray-600">
                            {filteredFAQ.length} pergunta{filteredFAQ.length !== 1 ? 's' : ''} encontrada{filteredFAQ.length !== 1 ? 's' : ''}
                          </p>
                        </div>

                        <div className="space-y-4">
                          {filteredFAQ.map((item, index) => (
                            <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-sm transition-shadow">
                              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-start gap-2">
                                <i className="ri-question-line text-teal-600 mt-1 flex-shrink-0"></i>
                                {item.question}
                              </h3>
                              <p className="text-gray-600 leading-relaxed pl-6">
                                {item.answer}
                              </p>
                            </div>
                          ))}
                        </div>

                        {filteredFAQ.length === 0 && (
                          <div className="text-center py-12">
                            <i className="ri-search-line text-4xl text-gray-400 mb-4"></i>
                            <h3 className="text-xl font-medium text-gray-900 mb-2">Nenhuma resposta encontrada</h3>
                            <p className="text-gray-600 mb-6">Tente buscar com outros termos ou entre em contato conosco</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </section>
          )
        }
      </div>

      {/* Métodos de Contato */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Precisa de Mais Ajuda?</h2>
            <p className="text-lg text-gray-600">Nossa equipe está pronta para atender você</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {contactMethods.map((method, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm text-center hover:shadow-md transition-shadow">
                <div className={`w-16 h-16 ${method.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <i className={`${method.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{method.title}</h3>
                <p className="text-gray-600 mb-4">{method.description}</p>
                <p className="text-lg font-medium text-gray-900 mb-4">{method.contact}</p>
                <Link
                  to="/contato"
                  className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors inline-block whitespace-nowrap cursor-pointer"
                >
                  {method.action}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recursos Adicionais */}
      {/* <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Recursos Úteis</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-text-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Manuais Técnicos</h3>
              <p className="text-gray-600 text-sm mb-4">Guias detalhados de instalação e manutenção</p>
              <Link
                to="/manuais"
                className="text-blue-600 hover:text-blue-700 font-medium text-sm cursor-pointer"
              >
                Acessar Manuais
              </Link>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-video-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Vídeos Tutoriais</h3>
              <p className="text-gray-600 text-sm mb-4">Aprenda com nossos especialistas</p>
              <Link
                to="/videos"
                className="text-green-600 hover:text-green-700 font-medium text-sm cursor-pointer"
              >
                Ver Vídeos
              </Link>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-community-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Comunidade</h3>
              <p className="text-gray-600 text-sm mb-4">Conecte-se com outros entusiastas</p>
              <Link
                to="/comunidade"
                className="text-purple-600 hover:text-purple-700 font-medium text-sm cursor-pointer"
              >
                Participar
              </Link>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-tools-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Ferramentas</h3>
              <p className="text-gray-600 text-sm mb-4">Calculadoras e utilitários técnicos</p>
              <Link
                to="/ferramentas"
                className="text-orange-600 hover:text-orange-700 font-medium text-sm cursor-pointer"
              >
                Usar Ferramentas
              </Link>
            </div>
          </div>
        </div>
      </section> */}

      {/* Formulário de Suporte */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Ainda precisa de ajuda?</h2>
              <p className="text-lg text-gray-600">
                Envie sua dúvida através do formulário abaixo e nossa equipe técnica entrará em contato em breve.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      id="nome"
                      name="nome"
                      required
                      value={formData.nome}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                      placeholder="Seu nome completo"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                      placeholder="seu@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="telefone" className="block text-sm font-medium text-gray-700 mb-2">
                      Telefone/WhatsApp
                    </label>
                    <input
                      type="tel"
                      id="telefone"
                      name="telefone"
                      value={formData.telefone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus-border-teal-500 text-sm"
                      placeholder="(11) 99154-2774"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="assunto" className="block text-sm font-medium text-gray-700 mb-2">
                      Assunto *
                    </label>
                    <select
                      id="assunto"
                      name="assunto"
                      required
                      value={formData.assunto}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus-border-teal-500 text-sm pr-8 cursor-pointer"
                    >
                      <option value="">Selecione o assunto</option>
                      <option value="instalacao">Dúvida sobre Instalação</option>
                      <option value="compatibilidade">Compatibilidade de Peças</option>
                      <option value="manutencao">Dúvida sobre Manutenção</option>
                      <option value="garantia">Questões de Garantia</option>
                      <option value="outro">Outro</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="mensagem" className="block text-sm font-medium text-gray-700 mb-2">
                    Descreva seu Problema/Dúvida *
                  </label>
                  <textarea
                    id="mensagem"
                    name="mensagem"
                    required
                    rows={5}
                    maxLength={500}
                    value={formData.mensagem}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus-border-teal-500 text-sm resize-none"
                    placeholder="Descreva sua dúvida ou problema técnico em detalhes..."
                  ></textarea>
                  <div className="text-right text-xs text-gray-500 mt-1">
                    {formData.mensagem.length}/500 caracteres
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || formData.mensagem.length > 500}
                  className="w-full bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar Solicitação de Suporte'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <i className="ri-check-circle-line text-green-600"></i>
                      <span className="text-green-800 font-medium">Formulário pronto para envio!</span>
                    </div>
                    <p className="text-green-700 text-sm mt-1">
                      Seu cliente de email foi aberto. Complete o envio clicando em "Enviar".
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <i className="ri-error-warning-line text-red-600"></i>
                      <span className="text-red-800 font-medium">Erro ao abrir cliente de email</span>
                    </div>
                    <p className="text-red-700 text-sm mt-1">
                      Envie um email diretamente para: comercial@globalzeit.com.br
                    </p>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-teal-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Precisa de suporte urgente?</h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Entre em contato direto com nossa equipe via WhatsApp ou telefone
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/5511991542774"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              WhatsApp
            </a>
            <Link
              to="/contato"
              className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors inline-block whitespace-nowrap"
            >
              Falar com Especialista
            </Link>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
